import { ComponentPropsWithRef, forwardRef, useContext } from 'react';

// Types
import type { Header } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import clsx from 'clsx';

// Components
import Icon from '@/components/base/Icon';
import { CompoundTableContext } from '@/components/base/Table/CompoundTable';
import TableHeaderOptionsButton from '@/components/base/Table/CompoundTable/TableHeader/TableHeaderOptionsButton';

type HeaderColumnProps = ComponentPropsWithRef<'th'> & {
    header: Header<unknown, unknown>;
    isDragging?: boolean;
    dragActivated?: boolean;
};

const commonClassName =
    'flex w-full h-full items-center justify-center px-m text-xs relative whitespace-nowrap group';

const HeaderColumn = forwardRef<HTMLTableCellElement, HeaderColumnProps>(
    ({ header, children, isDragging = false, dragActivated = false, ...restProps }, ref) => {
        const { table, columnIdHover, updateColumnIdHover } = useContext(CompoundTableContext);

        const nbColumns = table.getAllLeafColumns().length;
        const commonThClassName = clsx(
            header.column.columnDef.meta?.headerCellClassName,
            'h-14 border-b border-icoGray-300 p-0 dark:border-icoDarkMode-beton',
            {
                'border-r': header.index < nbColumns - 1,
                'sticky left-0 z-2': header.column.columnDef.meta?.isSticky
            }
        );

        // Return empty header column if isEmptyHeader meta is specified
        if (header.column.columnDef.meta?.isEmptyHeader) {
            return (
                <th
                    key={header.id}
                    data-testid={`column_${header.id}`}
                    className={clsx(commonThClassName, 'bg-white dark:bg-icoDarkMode-deepBlack')}>
                    <div className={commonClassName} />
                </th>
            );
        }

        const hoverCell = () => {
            if (!isDragging && !dragActivated) {
                updateColumnIdHover(header.index);
            }
        };

        const unHoverCell = () => {
            if (columnIdHover !== null) {
                updateColumnIdHover(null);
            }
        };

        const isColumnHovered = columnIdHover === header.index;

        return (
            <th className={commonThClassName}>
                <div
                    ref={ref}
                    onMouseEnter={hoverCell}
                    onMouseLeave={unHoverCell}
                    data-testid={`column_${header.id}`}
                    className={clsx(
                        commonClassName,
                        'bg-icoGray-100 dark:bg-icoDarkMode-deepBlack',
                        { 'z-10': isDragging }
                    )}
                    {...restProps}>
                    {isColumnHovered || isDragging ? (
                        <div className="absolute inset-0 bg-icoBlue bg-opacity-[0.15]" />
                    ) : null}
                    <div className="relative z-1 flex items-center justify-center space-x-2.5">
                        <Icon
                            icon="sort-by"
                            className={clsx('text-icoGray-500 dark:text-icoDarkMode-wolf', {
                                invisible: !header.column.getIsSorted()
                            })}
                        />
                        <div>{flexRender(header.column.columnDef.header, header.getContext())}</div>
                        <TableHeaderOptionsButton header={header} />
                    </div>
                    {children}
                </div>
            </th>
        );
    }
);

HeaderColumn.displayName = 'DraggableColumnHeader';

export default HeaderColumn;
